import {
    Alert,
    Button,
    CardHeader,
    Grid,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    Typography
} from "@mui/material";
import axios from "axios";
import * as React from "react";
import * as uuid from "uuid";
import { Config } from "../../../config/config";
import { Helpers } from "../../../utils/helpers";
import { PopupMessage } from "../../../utils/message";
import { TabPanel } from "../../Player";
import { IAPEntry } from "./entry";

interface IAPProps {
    activeUser: any;
    activeEnv: any;
    classes: any;
}

interface IAPState {
    list: IAPItem[];
    loading: boolean;

    activeTab: string;
}

export interface IAPItem {
    id: string;
    productId: string;
    price: number;
    type: string;
    platform: string;
}

const TYPES = {
    offer_pack: "Offer Packs",
    shop_pack: "Shop Packs",
    gold_pack: "Gem Packs",
    custom_pack: "Custom Packs",
};

const PLATFORMS = {
    "ios/android": "iOS/Android",
    "udp": "UDP",
    "ios": "iOS",
    "android": "Android",
    "all": "all"
};

export class IAPs extends React.Component<IAPProps, IAPState> {
    list: IAPItem[] = [];
    saving = false;

    constructor(props: IAPProps) {
        super(props);
        // List with grid items
        this.state = {
            list: [],
            loading: true,
            activeTab: "ios/android"
        };

        document.title = "Mavia | IAPs";
    }

    componentDidMount() {
        this.fetch();
    }

    fetch = () => {
        axios.get(`${Config.getInstance.apiUrl}/api/shop/iaps`, {
            headers: {
                Authorization: Helpers.getSessionId(),
                env: this.props.activeEnv,
            }
        }).then(response => {
            const list = (response.data || []).map((item) => ({
                id: uuid.v4(),
                ...item
            }));

            this.setState({
                loading: false,
                list,
            });

            this.list = [...list];
        });
    };

    async save() {
        if (this.saving) {
            return;
        }

        this.saving = true;

        try {
            await axios.post(`${Config.getInstance.apiUrl}/api/shop/iaps`, {
                items: this.list.map(item => ({
                    productId: item.productId,
                    price: +item.price,
                    type: item.type,
                    platform: item.platform
                }))
            }, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                    env: this.props.activeEnv,
                }
            });

            PopupMessage.success("IAPs successfully saved");
        } catch (e) {
            console.error(e);

            PopupMessage.error("Something went wrong");
        }

        this.saving = false;
    };

    add(platform: string, type: string) {
        this.list.push({
            id: uuid.v4(),
            productId: "",
            price: 0,
            platform,
            type
        });

        this.setState({
            list: [...this.list]
        });
    };

    onRemove = (item: IAPItem) => {
        const index = this.list.findIndex(_item => _item.id === item.id);

        this.list.splice(index, 1);

        this.setState({
            list: [...this.list]
        });
    };

    onUpdate = (item: IAPItem) => {
        const index = this.list.findIndex(_item => _item.id === item.id);

        this.list[index] = item;
    }

    onTabChange(tab: string) {
        this.setState({
            list: [...this.list],
            activeTab: tab
        });
    };

    getByPlatform = (platform: string) => this.state.list.filter(item => item.platform === platform);

    getByTypeAndPlatform = (platform: string, type: string) => this.state.list.filter(item => item.platform === platform && item.type === type);

    formatTableBody(items: any[], type) {
        if (this.state.loading) {
            return <TableRow key="loading-row">
                <TableCell colSpan={3}>
                    {Helpers.renderSkeletonRows(5, 50)}
                </TableCell>
            </TableRow>
        }

        if (items.length === 0) {
            return <TableRow key="records_not_found">
                <TableCell colSpan={3}>
                    <Alert severity="info">No {TYPES[type]} found</Alert>
                </TableCell>
            </TableRow>
        }

        return items.map(item => (
            <IAPEntry
                key={item.id}
                entry={item}
                classes={this.props.classes}
                onRemove={this.onRemove}
                onUpdate={this.onUpdate}
            />
        ));
    };

    pageContent(platform: string, type: string) {
        const items = this.getByTypeAndPlatform(platform, type);

        return <Paper elevation={1}>
            <h2 style={{padding: "20px 20px 0 20px"}}>
                {TYPES[type]}
            </h2>

            <div>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Product ID</TableCell>
                            <TableCell>Price ($)</TableCell>
                            <TableCell style={{width: 40}}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {this.formatTableBody(items, type)}
                    </TableBody>
                </Table>
            </div>

            {!this.state.loading && <div style={{padding: 20}}>
                <Button
                    disableElevation
                    variant={"contained"}
                    color={"primary"}
                    style={{width: "100%"}}
                    onClick={() => this.add(platform, type)}
                >
                    Add
                </Button>
            </div>}
        </Paper>;
    }

    render() {
        return <div className="contentPadding-full" style={{ marginTop: 24 }}>
            <Paper elevation={0} style={{ marginBottom: 20 }}>
                <CardHeader title="IAPs"/>

                <div style={{width: "100%"}}>
                    <div style={{padding: "0 24px"}}>
                        <div style={{ borderBottom: "1px solid #d8d8d8" }}>
                            <Tabs
                                value={this.state.activeTab}
                                onChange={(_event, tab) => this.onTabChange(tab)}
                                style={{marginTop: 25}}
                            >
                                {Object.entries(PLATFORMS).map(platform =>
                                    <Tab
                                        style={{minWidth: 120}}
                                        value={platform[0]}
                                        key={platform[0]}
                                        label={platform[1]}
                                        {...Helpers.a11yProps(platform[0])}
                                    />
                                )}
                            </Tabs>
                        </div>
                    </div>

                    {Object.entries(PLATFORMS).map(platform => <TabPanel value={this.state.activeTab} index={platform[0]}>
                        <Grid container spacing={3}>
                            {Object.entries(TYPES).map(type => <Grid item xs={3} key={type[0] + type[0]}>
                                {this.pageContent(platform[0], type[0])}
                            </Grid>)}
                        </Grid>
                    </TabPanel>)}
                </div>

                {!this.state.loading && <div style={{padding: 24, width: "100%"}}>
                    <Button disableElevation variant="contained" color="primary" onClick={() => this.save()}>
                        Save
                    </Button>
                </div>}
            </Paper>
        </div>;
    }
}
